import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Calculator as CalculatorIcon, Box, Store, TrendingUp, Search, Info } from 'lucide-react';
import shopeeMallDataEn from '../constants/shopeeMallDataEn.json';
import shopeeRegularDataEn from '../constants/shopeeRegularDataEn.json';

import {
  calculateSellingPrice,
  formatCurrency,
  formatPercentage,
  parseFeeString,
  CalculationInput,
  CalculationResult,
  FeeForCalc
} from '../constants/calculationFormulas';
import { showToast } from '../utils/toast';
import { NganhHang as BENganhHang } from '../services/cmsApi';
import { useChiPhi } from '../hooks/useChiPhi';
import { useNganhHang } from '../hooks/useNganhHang';
import Maintenance from './Maintenance';
import { useConsultation } from '../contexts/ConsultationContext';

interface CategoryData {
  [key: string]: string | CategoryData;
}

interface ProductSuggestion {
  name: string;
  fee: string;
  path: string[];
}

const CONSULTATION_STORAGE_KEY = 'hasConsultedBefore';

// Chuyển dữ liệu ngành hàng từ Backend -> dạng CategoryData lồng nhau mà calculator dùng.
// Ngành cha có loại con -> thành object (bỏ qua phiHoaHong '—' placeholder).
const beToCategoryData = (list: BENganhHang[]): CategoryData => {
  const out: CategoryData = {};
  for (const n of list) {
    const cat: CategoryData = {};
    for (const l of n.loaiSanPham) {
      if (l.loaiCon && l.loaiCon.length > 0) {
        const sub: CategoryData = {};
        l.loaiCon.forEach((c) => { sub[c.tenLoai] = c.phiHoaHong; });
        cat[l.tenLoai] = sub;
      } else {
        cat[l.tenLoai] = l.phiHoaHong;
      }
    }
    out[n.tenNganh] = cat;
  }
  return out;
};

const Calculator: React.FC = () => {
  // Phí + ngành hàng lấy từ Backend qua cache (version-gate + TTL 24h)
  const chiPhi = useChiPhi();
  const nganhHang = useNganhHang();

  // Ngành hàng VN (từ Backend) -> CategoryData. EN vẫn dùng JSON tĩnh (BE chưa có tên EN).
  const beCategories = useMemo(() => {
    if (!nganhHang.mall.length && !nganhHang.regular.length) return null;
    return { mall: beToCategoryData(nganhHang.mall), regular: beToCategoryData(nganhHang.regular) };
  }, [nganhHang.mall, nganhHang.regular]);

  const { t } = useTranslation();
  const [productName, setProductName] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<ProductSuggestion | null>(null);
  const [suggestions, setSuggestions] = useState<ProductSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isProductSelected, setIsProductSelected] = useState(false);

  const [formData, setFormData] = useState({
    cogs: '',
    shopeeType: 'mall' as 'mall' | 'regular',
    marketingCostPercent: '',
    desiredProfitPercent: '',
  });
  // Phí optional (Pi Ship, Voucher...) user bật/tắt: feeKey -> bool
  const [enabledOptional, setEnabledOptional] = useState<Record<string, boolean>>({});

  // Phí áp dụng cho shop đang chọn (lọc theo shopType + resolve giá trị)
  const feesForShop = useMemo<FeeForCalc[]>(() => {
    const list = chiPhi.data;
    if (!Array.isArray(list)) return []; // guard: cache cũ (shape khác) không làm trắng trang
    const shop = formData.shopeeType === 'mall' ? 'mall' : 'thuong';
    return list
      .filter((f) => f.isActive && f.shopTypes.includes(shop))
      .map((f) => ({
        feeKey: f.feeKey, label: f.label, unit: f.unit,
        optional: f.optional, optionalLabel: f.optionalLabel, capped: f.capped, maxValue: f.maxValue,
        value: shop === 'mall' ? f.valueMall : f.valueThuong,
      }));
  }, [chiPhi.data, formData.shopeeType]);
  const [calculationResult, setCalculationResult] = useState<CalculationResult | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [showMarketingTooltip, setShowMarketingTooltip] = useState(false);
  const [showProfitTooltip, setShowProfitTooltip] = useState(false);
  const [showCostPriceTooltip, setShowCostPriceTooltip] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(localStorage.getItem('i18nextLng') || 'vi');

  const { openPopup } = useConsultation();
  const [hasConsultedBefore, setHasConsultedBefore] = useState(() => {
    return localStorage.getItem(CONSULTATION_STORAGE_KEY) === 'true';
  });

  // Listen for consultation completion
  useEffect(() => {
    const handleConsultationCompleted = () => {
      setHasConsultedBefore(true);
      localStorage.setItem(CONSULTATION_STORAGE_KEY, 'true');
    };

    window.addEventListener('consultationCompleted', handleConsultationCompleted);
    return () => {
      window.removeEventListener('consultationCompleted', handleConsultationCompleted);
    };
  }, []);

  // Lấy dữ liệu sản phẩm theo loại Shopee và ngôn ngữ
  const getProductData = useCallback((): CategoryData => {
    const isEnglish = currentLanguage === 'en';
    const isMall = formData.shopeeType === 'mall';
    // EN: dùng JSON tĩnh (BE chưa lưu tên ngành tiếng Anh)
    if (isEnglish) {
      return (isMall ? shopeeMallDataEn : shopeeRegularDataEn) as CategoryData;
    }
    // VN: dữ liệu từ Backend (admin quản lý). Đang tải → rỗng (không fallback tĩnh)
    return beCategories ? (isMall ? beCategories.mall : beCategories.regular) : {};
  }, [currentLanguage, formData.shopeeType, beCategories]);

  // Tìm kiếm sản phẩm trong data
  const searchProducts = useCallback((searchTerm: string): ProductSuggestion[] => {
    if (!searchTerm.trim()) return [];
    
    const productData = getProductData();
    const results: ProductSuggestion[] = [];
    
    // Tách từ khóa thành các từ riêng biệt và chuẩn hóa (chỉ chuyển về chữ thường, giữ nguyên dấu)
    // Loại bỏ các dấu câu như phẩy, chấm, v.v. để tìm kiếm chính xác hơn
    const searchWords = searchTerm
      .toLowerCase()
      .trim()
      .replace(/[,.:;!?()[\]{}]/g, ' ') // Thay thế dấu câu bằng khoảng trắng
      .split(/\s+/)
      .filter(word => word.length > 0);
    
    if (searchWords.length === 0) return [];
    
    const searchInCategory = (data: CategoryData, path: string[] = []) => {
      for (const [key, value] of Object.entries(data)) {
        const currentPath = [...path, key];
        
        if (typeof value === 'string') {
          // Đây là sản phẩm (có giá trị %)
          // Chuẩn hóa tên sản phẩm: lowercase và loại bỏ dấu câu giống như search term
          const productNameNormalized = key
            .toLowerCase()
            .replace(/[,.:;!?()[\]{}]/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
          
          // Kiểm tra xem tên sản phẩm có chứa ít nhất 1 từ khóa không
          const hasMatch = searchWords.some(word => productNameNormalized.includes(word));
          
          if (hasMatch) {
            results.push({
              name: key,
              fee: value,
              path: currentPath
            });
          }
        } else {
          // Đây là danh mục con, tiếp tục tìm kiếm
          searchInCategory(value, currentPath);
        }
      }
    };
    
    searchInCategory(productData);
    
    // Sắp xếp theo độ tương đồng
    return results.sort((a, b) => {
      // Chuẩn hóa tên giống như trong hàm search
      const aNameNormalized = a.name.toLowerCase().replace(/[,.:;!?()[\]{}]/g, ' ').replace(/\s+/g, ' ').trim();
      const bNameNormalized = b.name.toLowerCase().replace(/[,.:;!?()[\]{}]/g, ' ').replace(/\s+/g, ' ').trim();
      
      // Đếm số từ khóa khớp
      const aMatchCount = searchWords.filter(word => aNameNormalized.includes(word)).length;
      const bMatchCount = searchWords.filter(word => bNameNormalized.includes(word)).length;
      
      // Sắp xếp theo số từ khóa khớp (nhiều hơn trước)
      if (aMatchCount !== bMatchCount) {
        return bMatchCount - aMatchCount;
      }
      
      // Nếu số từ khóa khớp bằng nhau, ưu tiên sản phẩm có tên bắt đầu bằng từ khóa
      const aStartsWith = searchWords.some(word => aNameNormalized.startsWith(word));
      const bStartsWith = searchWords.some(word => bNameNormalized.startsWith(word));
      
      if (aStartsWith && !bStartsWith) return -1;
      if (!aStartsWith && bStartsWith) return 1;
      
      // Cuối cùng sắp xếp theo tên
      return a.name.localeCompare(b.name);
    }).slice(0, 10); // Giới hạn 10 kết quả
  }, [getProductData]);

  // Debounced search
  useEffect(() => {
    // Nếu vừa chọn sản phẩm, không search
    if (isProductSelected) {
      setIsProductSelected(false);
      return;
    }

    if (!productName.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsSearching(true);
    const timeoutId = setTimeout(() => {
      const results = searchProducts(productName);
      setSuggestions(results);
      setShowSuggestions(results.length > 0);
      setIsSearching(false);
    }, 500); // Debounce 500ms

    return () => {
      clearTimeout(timeoutId);
      setIsSearching(false);
    };
  }, [productName, searchProducts, isProductSelected]);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.product-search-container')) {
        setShowSuggestions(false);
      }
    };

    if (showSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSuggestions]);

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      const newLanguage = localStorage.getItem('i18nextLng') || 'vi';
      if (newLanguage !== currentLanguage) {
        setCurrentLanguage(newLanguage);
        setProductName('');
        setSelectedProduct(null);
        setSuggestions([]);
        setShowSuggestions(false);
        setShowResult(false);
         setCalculationResult(null);
       }
    };

    // Check for language changes periodically
    const interval = setInterval(handleLanguageChange, 100);
    
    return () => clearInterval(interval);
  }, [currentLanguage]);

  // Xử lý thay đổi loại Shopee
  const handleShopeeTypeChange = (type: 'mall' | 'regular') => {
    setFormData(prev => ({ ...prev, shopeeType: type }));
    setProductName('');
    setSelectedProduct(null);
    setSuggestions([]);
    setShowSuggestions(false);
    setShowResult(false);
    setCalculationResult(null);
    clearError('product');
  };

  // Xử lý chọn sản phẩm từ gợi ý
  const handleProductSelect = (product: ProductSuggestion) => {
    setIsProductSelected(true); // Đánh dấu đã chọn để không trigger search
    setSelectedProduct(product);
    setProductName(product.name);
    setSuggestions([]);
    setShowSuggestions(false);
    setShowResult(false);
    setCalculationResult(null);
    clearError('product');
  };
    
  // Xử lý thay đổi tên sản phẩm
  const handleProductNameChange = (value: string) => {
    setProductName(value);
    if (!value.trim()) {
      setSelectedProduct(null);
      setSuggestions([]);
      setShowSuggestions(false);
    }
    clearError('product');
  };


  // Tính toán
  const handleCalculate = () => {
    // Validate và lưu errors
    const newErrors: {[key: string]: string} = {};

    if (!selectedProduct) {
      newErrors.product = t('calculator.validation.productRequired');
    }

    const cogsValue = parseFloat(parseMoneyInput(formData.cogs));
    if (!formData.cogs || isNaN(cogsValue) || cogsValue <= 0) {
      newErrors.cogs = t('calculator.validation.costPriceRequired');
    }

    if (!formData.desiredProfitPercent || parseFloat(formData.desiredProfitPercent) < 0) {
      newErrors.desiredProfitPercent = t('calculator.validation.profitRequired');
    }

    if (formData.marketingCostPercent && parseFloat(formData.marketingCostPercent) < 0) {
      newErrors.marketingCostPercent = t('calculator.validation.marketingCostInvalid');
    }

    setErrors(newErrors);

    // Thêm class error cho các input có lỗi
    Object.keys(newErrors).forEach(fieldName => {
      const inputElement = document.querySelector(`[name="${fieldName}"]`) as HTMLElement;
      if (inputElement) {
        inputElement.classList.add('error');
      }
    });

    // Xóa class error cho các input không có lỗi
    const allInputs = document.querySelectorAll('input, select');
    allInputs.forEach(input => {
      const inputName = (input as HTMLInputElement).name;
      if (inputName && !newErrors[inputName]) {
        input.classList.remove('error');
      }
    });

    if (Object.keys(newErrors).length > 0) {
      // Hiển thị toast với tất cả các lỗi
      const errorMessages = Object.values(newErrors).map(msg => '- ' + msg);
      const message = errorMessages.join('\n');
      console.log("Error messages:", message);
      showToast(message, 'error');
      return;
    }
    const input: CalculationInput = {
      cogs: parseFloat(parseMoneyInput(formData.cogs)),
      productFeePercent: parseFeeString(selectedProduct!.fee),
      shopeeType: formData.shopeeType,
      marketingCostPercent: formData.marketingCostPercent ? parseFloat(formData.marketingCostPercent) : undefined,
      desiredProfitPercent: parseFloat(formData.desiredProfitPercent),
      fees: feesForShop,
      enabledOptional,
    };

    const result = calculateSellingPrice(input);
    setCalculationResult(result);
    setShowResult(true);

    if (result.isValid) {
      showToast(t('common.success'), 'success');
      // Scroll to result with offset to ensure full visibility
      setTimeout(() => {
        const resultSection = document.getElementById('resultSection');
        if (resultSection) {
          const headerHeight = 80; // Approximate header height
          const offset = headerHeight -70; // Add some padding
          const elementTop = resultSection.offsetTop - offset;

          window.scrollTo({
            top: elementTop,
            behavior: 'smooth'
          });
        }
      }, 100);
    } else {
      showToast(result.errorMessage || t('common.error'), 'error');
    }
  };

  // Format input tiền - thêm dấu chấm phân cách hàng nghìn
  const formatMoneyInput = (value: string): string => {
    // Loại bỏ tất cả ký tự không phải số
    const numericValue = value.replace(/[^\d]/g, '');

    // Nếu rỗng, trả về chuỗi rỗng
    if (!numericValue) return '';

    // Thêm dấu chấm phân cách hàng nghìn
    return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  // Parse số từ format hiển thị về số thuần
  const parseMoneyInput = (value: string): string => {
    return value.replace(/\./g, '');
  };

  // Format input phần trăm
  const formatPercentageInput = (value: string): string => {
    return value.replace(/[^\d.]/g, '');
  };

  // Xóa lỗi khi user bắt đầu nhập
  const clearError = (fieldName: string) => {
    if (errors[fieldName]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });

      // Xóa class error
      const inputElement = document.querySelector(`[name="${fieldName}"]`) as HTMLElement;
      if (inputElement) {
        inputElement.classList.remove('error');
      }
    }
  };

  // BE gọi được config (site đã qua gate) nhưng data phí/ngành lỗi bất thường → bảo trì
  if (chiPhi.error || nganhHang.error) {
    return <Maintenance />;
  }

  return (
    <div className="calculator-page">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <h1 style={{ whiteSpace: 'pre-line' }}>
            <CalculatorIcon className="header-icon" />
            {t('calculator.title')}
          </h1>
          <p style={{ whiteSpace: 'pre-line', fontStyle: 'italic' }}>{t('calculator.subtitle')}</p>
        </div>

        <div className="calculator-content">
          {/* Thông tin sản phẩm - Full width */}
          <div className="form-section wide">
              <div className="section-title">
                <Box className="section-icon" />
                {t('calculator.form.productInfo')}
              </div>

            <div className="settings-cost-layout">
              {/* Loại Shopee và Tên sản phẩm */}
              {/* Loại Shopee */}
                <div className="form-group inline">
                <label>{t('calculator.form.shopeeType')}<span className="required">*</span></label>
                <div className="radio-group">
                  <div className="radio-item">
                    <input
                      type="radio"
                      id="shopeeMall"
                      name="shopeeType"
                      value="mall"
                      checked={formData.shopeeType === 'mall'}
                      onChange={() => handleShopeeTypeChange('mall')}
                    />
                    <label htmlFor="shopeeMall">{t('calculator.form.shopeeMall')}</label>
                  </div>
                  <div className="radio-item">
                    <input
                      type="radio"
                      id="shopeeRegular"
                      name="shopeeType"
                      value="regular"
                      checked={formData.shopeeType === 'regular'}
                      onChange={() => handleShopeeTypeChange('regular')}
                    />
                    <label htmlFor="shopeeRegular">{t('calculator.form.shopeeRegular')}</label>
                  </div>
                </div>
              </div>

                {/* Tên sản phẩm */}
                <div className="form-group">
                  <label>{t('calculator.form.productName')}<span className="required">*</span></label>
                  <div className="product-search-container">
                    <div className="search-input-wrapper" style={{ position: 'relative' }}>
                      <input
                        name="product"
                        type="text"
                        value={productName}
                        onChange={(e) => handleProductNameChange(e.target.value)}
                        placeholder={t('calculator.form.productNamePlaceholder')}
                        className={`search-input ${errors.product ? 'error' : ''}`}
                      />
                      <Search className="search-icon" />
                      {isSearching && <div className="searching-indicator">{t('calculator.form.searching')}</div>}
                    </div>
                    
                    {/* Dropdown gợi ý - chỉ hiện khi chưa chọn sản phẩm */}
                    {showSuggestions && suggestions.length > 0 && !selectedProduct && (
                      <div className="suggestions-dropdown">
                        {suggestions.map((product, index) => (
                          <div
                            key={index}
                            className="suggestion-item"
                            onClick={() => handleProductSelect(product)}
                          >
                            <div className="suggestion-name">{product.name}</div>
                            <div className="suggestion-path">{product.path.join(' > ')}</div>
                            <div className="suggestion-fee">{product.fee}</div>
                          </div>
                        ))}
                </div>
              )}
                  </div>
                  {errors.product && <div className="error-message">{errors.product}</div>}
                </div>



                        {/* Hiển thị sản phẩm đã chọn */}
          {selectedProduct && (
            <div className="selected-product">
              <div className="selected-product-info">
                <strong>{t('calculator.form.selectedProduct')}</strong> {selectedProduct.name}
              </div>
              <div className="selected-product-path">
                <strong>{t('calculator.form.category')}</strong> {selectedProduct.path.join(' > ')}
              </div>
              <div className="selected-product-fee">
                <strong>{t('calculator.form.fixedFee')}</strong> {selectedProduct.fee}
              </div>
            </div>
          )}

              {/* Giá vốn sản phẩm */}
              <div className="form-group">
                <label>
                  {t('calculator.form.costPrice')}
                  <Info 
                    className="info-icon" 
                    onMouseEnter={() => setShowCostPriceTooltip(true)}
                    onMouseLeave={() => setShowCostPriceTooltip(false)}
                  />
                  {showCostPriceTooltip && (
                    <div className="tooltip">
                      {t('calculator.tooltips.costPrice')}
                      <div className="tooltip-arrow"></div>
                    </div>
                  )}
                  <span className="required">*</span>
                </label>
                <input
                  name="cogs"
                  type="text"
                  value={formData.cogs}
                  onChange={(e) => {
                    clearError('cogs');
                    setFormData(prev => ({ 
                      ...prev, 
                      cogs: formatMoneyInput(e.target.value) 
                    }));
                  }}
                  placeholder={t('calculator.form.costPricePlaceholder')}
                  className={errors.cogs ? 'error' : ''}
                />
                {errors.cogs && <div className="error-message">{errors.cogs}</div>}
              </div>
              </div>
            </div>

          {/* Cài đặt Shopee và Chi phí dự tính - Combined */}
          <div className="form-section wide settings-section-container">
            <div className="settings-cost-layout">
            {/* Cài đặt Shopee */}
              <div className="settings-section">
              <div className="section-title">
                <Store className="section-icon" />
                {t('calculator.settings.title')}
              </div>

              {/* Phí tuỳ chọn (Pi Ship, Voucher Xtra... — admin cấu hình) */}
              {feesForShop.some((f) => f.optional) && (
                <div className="form-group inline">
                  <label>{t('calculator.settings.optionalFees')}</label>
                  <div className="checkbox-group">
                    {feesForShop.filter((f) => f.optional).map((f) => (
                      <div className="checkbox-item" key={f.feeKey}>
                        <input
                          type="checkbox"
                          id={`opt_${f.feeKey}`}
                          checked={!!enabledOptional[f.feeKey]}
                          onChange={(e) => setEnabledOptional(prev => ({ ...prev, [f.feeKey]: e.target.checked }))}
                        />
                        <label htmlFor={`opt_${f.feeKey}`}>{f.optionalLabel || f.label}</label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
          </div>

          {/* Chi phí dự tính */}
              <div className="cost-section">
            <div className="section-title">
              <TrendingUp className="section-icon" />
              {t('calculator.costs.title')}
            </div>

                <div className="cost-inputs">
                  {/* Lợi nhuận mong muốn và Chi phí marketing */}
                  <div className="cost-inputs-row">
              {/* Lợi nhuận mong muốn */}
                    <div className="form-group inline">
                <label>
                  {t('calculator.costs.desiredProfitPercent')}
                  <Info 
                    className="info-icon" 
                    onMouseEnter={() => setShowProfitTooltip(true)}
                    onMouseLeave={() => setShowProfitTooltip(false)}
                  />
                                    {showProfitTooltip && (
                    <div className="tooltip">
                      {t('calculator.tooltips.profit')}
                      <div className="tooltip-arrow"></div>
                    </div>
                  )}
                  <span className="required">*</span>
                </label>
                <input
                  name="desiredProfitPercent"
                  type="text"
                  value={formData.desiredProfitPercent}
                  onChange={(e) => {
                    clearError('desiredProfitPercent');
                    setFormData(prev => ({ 
                      ...prev, 
                      desiredProfitPercent: formatPercentageInput(e.target.value) 
                    }));
                  }}
                  placeholder={t('calculator.costs.desiredProfitPercentPlaceholder')}
                  className={errors.desiredProfitPercent ? 'error' : ''}
                />
                {errors.desiredProfitPercent && <div className="error-message">{errors.desiredProfitPercent}</div>}
              </div>

              {/* Chi phí marketing */}
              <div className="form-group inline">
                <label>
                  {t('calculator.costs.adCost')}
                  <Info 
                    className="info-icon" 
                    onMouseEnter={() => setShowMarketingTooltip(true)}
                    onMouseLeave={() => setShowMarketingTooltip(false)}
                  />
                  {showMarketingTooltip && (
                    <div className="tooltip">
                      {t('calculator.tooltips.marketing')}
                      <div className="tooltip-arrow"></div>
                    </div>
                  )}
                </label>
                <input
                  name="marketingCostPercent"
                  type="text"
                  value={formData.marketingCostPercent}
                  onChange={(e) => {
                    clearError('marketingCostPercent');
                    setFormData(prev => ({ 
                      ...prev, 
                      marketingCostPercent: formatPercentageInput(e.target.value) 
                    }));
                  }}
                  placeholder={t('calculator.costs.adCostPlaceholder')}
                  className={errors.marketingCostPercent ? 'error' : ''}
                />
                {errors.marketingCostPercent && <div className="error-message">{errors.marketingCostPercent}</div>}
              </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Nút tính toán - Nằm ngoài box */}
            <div className="calculate-button-container">
            <button 
              className="calculate-btn" 
              onClick={handleCalculate}
            >
              <TrendingUp className="btn-icon" />
              {t('calculator.form.calculate')}
            </button>
            </div>
          </div>

          {/* Kết quả tính toán */}
          {showResult && calculationResult && (
            <div id="resultSection" className="result-section" style={{ position: 'relative' }}>
              <div className="result-header">
                <h2>
                  <CalculatorIcon className="result-icon" />
                  {t('calculator.results.title')}
                </h2>
              </div>

              <div className="result-content">
                {!calculationResult.isValid ? (
                  <div className="error-message" style={{ textAlign: 'center', fontSize: '1.1rem', padding: '20px' }}>
                    {calculationResult.errorMessage}
                  </div>
                ) : (
                  <div className="result-grid">
                    {/* Chi phí Shopee */}
                    <div className="result-card">
                      <h3>
                        <Store className="card-icon" />
                        {t('calculator.results.estimatedCosts')}
                      </h3>
                      <table className="result-table">
                        <tbody>
                          <tr>
                            <td>{t('calculator.results.productFee')}:</td>
                            <td className="highlight-value" style={{ textAlign: 'right', fontSize: '0.9rem' }}>{formatCurrency(calculationResult.productFee)}</td>
                          </tr>
                          {calculationResult.fees.map((fee) => (
                            <tr key={fee.feeKey}>
                              <td>{fee.label}:</td>
                              <td className="highlight-value" style={{ textAlign: 'right', fontSize: '0.9rem' }}>{formatCurrency(fee.amount)}</td>
                            </tr>
                          ))}
                          <tr style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa', borderTop: '2px solid #dee2e6' }}>
                            <td>{t('calculator.results.totalCosts')}:</td>
                            <td className="highlight-value" style={{ fontWeight: 'bold', textAlign: 'right', fontSize: '1.1rem' }}>
                              {formatCurrency(
                                calculationResult.productFee +
                                calculationResult.fees.reduce((s, f) => s + f.amount, 0)
                              )}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Tổng kết */}
                    <div className="result-card">
                      <h3>
                        <TrendingUp className="card-icon" />
                        {t('calculator.results.costBreakdown')}
                      </h3>
                      <table className="result-table">
                        <thead>
                          <tr>
                            <th style={{ minWidth: 180 }}>{t('calculator.results.item')}</th>
                            <th style={{ minWidth: 120 }}>{t('calculator.results.amount')}</th>
                            <th style={{ minWidth: 90 }}>{t('calculator.results.percentageOnPrice')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {/* Hàng 1: Giá vốn sản phẩm */}
                          <tr>
                            <td>{t('calculator.results.costPrice')}</td>
                            <td className="highlight-value" style={{ textAlign: 'right', fontSize: '0.9rem' }}>{formatCurrency(calculationResult.cogs)}</td>
                            <td className="highlight-value" style={{ textAlign: 'right', fontSize: '0.9rem' }}>{formatPercentage(calculationResult.finalPrice ? (calculationResult.cogs / calculationResult.finalPrice) * 100 : 0)}</td>
                          </tr>
                          {/* Hàng 2: Chi phí dự kiến */}
                          <tr>
                            <td colSpan={3} style={{ fontWeight: 600, color: '#2c3e50', backgroundColor: '#f8f9fa' }}>{t('calculator.results.estimatedCosts')}:</td>
                          </tr>
                          {/* Hàng20.1: Chi phí Shopee (tổng các phí Shopee) */}
                          <tr>
                            <td style={{ paddingLeft: 20 }}>{t('calculator.results.shopeeCosts')}:</td>
                            <td className="highlight-value" style={{ textAlign: 'right', fontSize: '0.9rem' }}>{formatCurrency(
                              calculationResult.productFee + calculationResult.fees.reduce((s, f) => s + f.amount, 0)
                            )}</td>
                            <td className="highlight-value" style={{ textAlign: 'right', fontSize: '0.9rem' }}>{formatPercentage(calculationResult.finalPrice ? (
                              (calculationResult.productFee + calculationResult.fees.reduce((s, f) => s + f.amount, 0)) / calculationResult.finalPrice
                            ) * 100 : 0)}</td>
                          </tr>
                          {/* Hàng 2.2: Chi phí Marketing (nếu có) */}
                          {calculationResult.marketingCost > 0 && (
                            <tr>
                              <td style={{ paddingLeft: 20 }}>{t('calculator.costs.adCost')}:</td>
                              <td className="highlight-value" style={{ textAlign: 'right', fontSize: '0.9rem' }}>{formatCurrency(calculationResult.marketingCost)}</td>
                              <td className="highlight-value" style={{ textAlign: 'right', fontSize: '0.9rem' }}>{formatPercentage(calculationResult.finalPrice ? (calculationResult.marketingCost / calculationResult.finalPrice) * 100 : 0)}</td>
                            </tr>
                          )}
                          {/* Hàng 3: Lợi nhuận */}
                          <tr>
                            <td>{t('calculator.results.profit')}:</td>
                            <td className={`highlight-value ${calculationResult.profit >= 0 ? 'profit-positive' : 'profit-negative'}`} style={{ textAlign: 'right', fontSize: '0.9rem' }}>{formatCurrency(calculationResult.profit)}</td>
                            <td className={`highlight-value ${calculationResult.profit >= 0 ? 'profit-positive' : 'profit-negative'}`} style={{ textAlign: 'right', fontSize: '0.9rem' }}>{formatPercentage(calculationResult.finalPrice ? (calculationResult.profit / calculationResult.finalPrice) * 100 : 0)}</td>
                          </tr>
                          {/* Hàng cuối: Giá bán cuối cùng */}
                          <tr className="final-price-row">
                            <td><strong>{t('calculator.results.finalPrice')}:</strong></td>
                            <td className="highlight-value" style={{ fontSize: '1rem', fontWeight: 'bold', textAlign: 'right' }}>{formatCurrency(calculationResult.finalPrice)}</td>
                            <td className="highlight-value" style={{ fontSize: '1rem', fontWeight: 'bold', textAlign: 'right', whiteSpace: 'nowrap' }}>
                              {formatPercentage(100)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              {/* Overlay mờ nếu chưa tư vấn */}
              {!hasConsultedBefore && (
                <div className="consultation-overlay">
                  <div className="consultation-notice">
                    <h3>🔒 Nhận ngay bản phân tích chi phí & lợi nhuận dành riêng cho bạn</h3>
                    <p>Chúng tôi sẽ gửi kết quả chi tiết giúp bạn ước tính chính xác hiệu quả bán hàng.</p>
                    <button
                      className="consultation-btn"
                      onClick={() => openPopup('calculator')}
                    >
                      Nhận kết quả của tôi
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default Calculator; 